// =============================
// src/components/purchase/AddPurchaseModal.jsx
// =============================
import React, { useEffect, useMemo, useRef, useState } from "react";
import { X, Plus, Trash2, Search } from "lucide-react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { createPortal } from "react-dom";
import toast from "react-hot-toast";

import { createPurchase } from "../../api/purchases";
import { listSuppliers } from "../../api/master";
import { getProducts } from "../../api/products";
import { getMyProfile } from "../../api/users";

const toNum = (v) => Number(v || 0);

/** Stock units per pack, or null when the product isn't bought in packs. */
function packSizeOf(product) {
  const size = Number(product?.pack_size);
  return Number.isFinite(size) && size > 1 ? size : null;
}

/** Label for what is INSIDE one pack ("Batang", "Pcs"), not the pack itself. */
function packLabelOf(product) {
  return product?.pack_label?.trim() || "isi";
}

/**
 * Pre-fill for a PO line's price.
 *
 * Purchasing always happens in the product's stock unit — if a product is
 * bought by the pack, its Unit *is* "Pack" and cost_price is already the pack
 * price. So no scaling happens here, and the old pack/unit dropdown is gone:
 * that dropdown existed only because stock used to be counted in pieces while
 * buying happened in packs, which is exactly the mismatch that confused buyers.
 *
 * Returns "" (not 0) when no cost is on file: a blank field prompts for the real
 * supplier price, whereas 0 looks like an answer and books zero-cost stock.
 */
function packAwareCost(product) {
  const cost = Number(product?.cost_price);
  if (!Number.isFinite(cost) || cost <= 0) return "";

  return cost;
}

/** Satuan from product master (same source as Add Product). */
function productUnitLabel(p) {
  if (!p) return "—";
  return (
    p.unit_name ||
    p.unit?.name ||
    p.unit?.label ||
    p.uom ||
    (typeof p.unit === "string" ? p.unit : null) ||
    "—"
  );
}

export default function AddPurchaseModal({ open, onClose, storeLocationId: storeLocationIdProp = null }) {
  const qc = useQueryClient();

  // --- header form state
  const [supplierId, setSupplierId] = useState("");
  const [orderDate, setOrderDate] = useState(
    new Date().toISOString().slice(0, 10)
  );
  const [expectedDate, setExpectedDate] = useState("");
  const [notes, setNotes] = useState("");

  // --- items
  const [items, setItems] = useState([
    { product_id: "", qty_order: "", unit_price: "", discount: "0", tax: "0" },
  ]);

  // --- dropdown state
  const [activeProductRow, setActiveProductRow] = useState(null);
  const [productSearch, setProductSearch] = useState("");
  const [productMenuPos, setProductMenuPos] = useState(null);

  const [supplierOpen, setSupplierOpen] = useState(false);
  const [supplierSearch, setSupplierSearch] = useState("");

  // debounced search (server-side), supaya tidak nge-spam API tiap keystroke
  const [debouncedProductSearch, setDebouncedProductSearch] = useState("");
  const [debouncedSupplierSearch, setDebouncedSupplierSearch] = useState("");

  useEffect(() => {
    const t = setTimeout(() => setDebouncedProductSearch(productSearch.trim()), 250);
    return () => clearTimeout(t);
  }, [productSearch]);

  useEffect(() => {
    const t = setTimeout(() => setDebouncedSupplierSearch(supplierSearch.trim()), 250);
    return () => clearTimeout(t);
  }, [supplierSearch]);

  // cache produk/supplier yang sudah pernah dipilih, supaya nama tetap terlihat
  // walau hasil query sekarang berubah karena search berubah.
  const [productCache, setProductCache] = useState({}); // { [id]: product }
  const [supplierCache, setSupplierCache] = useState({}); // { [id]: supplier }

  // refs buat anchor tombol product
  const productBtnRefs = useRef([]);

  // reset saat open
  useEffect(() => {
    if (!open) return;
    setSupplierId("");
    setOrderDate(new Date().toISOString().slice(0, 10));
    setExpectedDate("");
    setNotes("");
    setItems([
      { product_id: "", qty_order: "", unit_price: "", discount: "0", tax: "0" },
    ]);
    setActiveProductRow(null);
    setProductSearch("");
    setDebouncedProductSearch("");
    setProductMenuPos(null);
    setSupplierOpen(false);
    setSupplierSearch("");
    setDebouncedSupplierSearch("");
    setProductCache({});
    setSupplierCache({});
  }, [open]);

  // --- profile user (ambil store_location_id untuk filter produk)
  const { data: me } = useQuery({
    queryKey: ["me"],
    queryFn: () => getMyProfile(),
    staleTime: 5 * 60 * 1000,
  });

  const storeId =
    storeLocationIdProp ??
    me?.store_location_id ??
    me?.storeLocation?.id ??
    me?.store_location?.id ??
    me?.store?.id ??
    null;

  // --- queries
  // Server-side search: backend cap per_page=100 (suppliers) & 200 (products),
  // jadi kalau dataset banyak kita HARUS kirim `search` ke BE, bukan filter di FE.
  const { data: supResp, isLoading: supLoading } = useQuery({
    enabled: open,
    queryKey: ["suppliers", { search: debouncedSupplierSearch }],
    queryFn: ({ signal, queryKey }) => {
      const { search } = queryKey[1] || {};
      return listSuppliers(
        { page: 1, per_page: 100, search: search || "" },
        signal
      );
    },
    keepPreviousData: true,
  });

  const { data: prodResp, isLoading: prodLoading } = useQuery({
    enabled: open,
    queryKey: ["products", { storeId, search: debouncedProductSearch }],
    queryFn: ({ signal, queryKey }) => {
      const { storeId, search } = queryKey[1] || {};
      return getProducts(
        {
          page: 1,
          per_page: 100,
          search: search || "",
          store_location_id: storeId || undefined, // ⬅️ filter berdasarkan store
        },
        signal
      );
    },
    keepPreviousData: true,
  });

  // unwrap
  const suppliers = supResp?.items || supResp?.data || supResp || [];
  const products = prodResp?.items || prodResp?.data || prodResp || [];

  // search sudah dikerjakan di server, jadi pakai langsung.
  const filteredSuppliers = suppliers || [];
  const filteredProducts = products || [];

  // info kalau hasil dipotong oleh pagination (BE cap 100/200 per page)
  const productTotal = Number(prodResp?.meta?.total ?? products.length);
  const productTruncated = productTotal > products.length;

  // --- items ops
  const addRow = () =>
    setItems((rows) => [
      ...rows,
      { product_id: "", qty_order: "", unit_price: "", discount: "0", tax: "0" },
    ]);

  const removeRow = (idx) =>
    setItems((rows) => rows.filter((_, i) => i !== idx));

  const changeRow = (idx, patch) =>
    setItems((rows) => rows.map((r, i) => (i === idx ? { ...r, ...patch } : r)));

  const onSelectProduct = (idx, productId) => {
    const p = products.find((x) => String(x.id) === String(productId));
    if (p) setProductCache((c) => ({ ...c, [String(p.id)]: p }));
    setItems((rows) =>
      rows.map((r, i) =>
        i === idx
          ? {
              ...r,
              product_id: productId,
              // Pre-fill from the COST, never the sell price.
              // `purchase_price` has never existed as a column, so the old
              // `p.purchase_price ?? p.price` fallback silently stamped the
              // sell price onto every PO line — 1,042 of them in production,
              // which is what made COGS equal revenue and zeroed all margins.
              // Blank when no cost is known, so the buyer types the real one.
              unit_price: r.unit_price ? r.unit_price : packAwareCost(p),
            }
          : r
      )
    );
    setActiveProductRow(null);
    setProductMenuPos(null);
    setProductSearch("");
    setDebouncedProductSearch("");
  };

  const onSelectSupplier = (s) => {
    if (s?.id != null) setSupplierCache((c) => ({ ...c, [String(s.id)]: s }));
    setSupplierId(s?.id ?? "");
    setSupplierOpen(false);
    setSupplierSearch("");
    setDebouncedSupplierSearch("");
  };

  // helper lookup: cek hasil query dulu, fallback ke cache pilihan sebelumnya.
  const findProduct = (id) =>
    products.find((p) => String(p.id) === String(id)) ||
    productCache[String(id)] ||
    null;

  const findSupplier = (id) =>
    suppliers.find((s) => String(s.id) === String(id)) ||
    supplierCache[String(id)] ||
    null;

  // buka dropdown product → hitung posisi dari viewport
  const openProductMenu = (idx) => {
    const btn = productBtnRefs.current[idx];
    if (!btn) {
      setActiveProductRow(idx);
      return;
    }
    const rect = btn.getBoundingClientRect();
    setProductMenuPos({
      left: rect.left,
      top: rect.bottom + 4, // 4px di bawah tombol
      width: rect.width,
    });
    setActiveProductRow(idx);
    setProductSearch("");
    setDebouncedProductSearch("");
  };

  // tutup dropdown product saat resize/scroll besar
  useEffect(() => {
    if (activeProductRow == null) return;
    const handler = () => {
      setActiveProductRow(null);
      setProductMenuPos(null);
      setProductSearch("");
      setDebouncedProductSearch("");
    };
    window.addEventListener("resize", handler);
    window.addEventListener("scroll", handler);
    return () => {
      window.removeEventListener("resize", handler);
      window.removeEventListener("scroll", handler);
    };
  }, [activeProductRow]);

  // subtotal per item & grand total
  // Qty and price are always in the SAME unit (both per pack, or both per
  // stock unit), so the product is the line total either way — no scaling here.
  const subTotals = useMemo(
    () =>
      items.map((it) => {
        const base = toNum(it.qty_order) * toNum(it.unit_price);
        return base - toNum(it.discount) + toNum(it.tax);
      }),
    [items]
  );
  const grandTotal = subTotals.reduce(
    (a, b) => a + (Number.isFinite(b) ? b : 0),
    0
  );

  // --- submit
  const mutation = useMutation({
    mutationFn: (payload) => createPurchase(payload),
    onSuccess: () => {
      toast.success("Draft PO berhasil dibuat");
      qc.invalidateQueries({ queryKey: ["purchases"] });
      onClose?.();
    },
    onError: (e) => {
      const msg =
        e?.response?.data?.message ||
        e?.response?.data?.error ||
        e?.message ||
        "Gagal membuat draft purchase";
      toast.error(msg);
    },
  });

  const validate = () => {
    if (!supplierId) return "Supplier wajib dipilih";
    if (!orderDate) return "Order date wajib diisi";
    if (!items.length) return "Tambahkan minimal 1 item";
    for (const [i, it] of items.entries()) {
      if (!it.product_id) return `Item #${i + 1}: product wajib dipilih`;
      if (!it.qty_order || Number(it.qty_order) <= 0)
        return `Item #${i + 1}: qty_order harus > 0`;
      if (!it.unit_price || Number(it.unit_price) <= 0)
        return `Item #${i + 1}: unit_price harus > 0`;
    }
    return null;
  };

  const submit = () => {
    const err = validate();
    if (err) return toast.error(err);

    if (!storeId) {
      return toast.error("Pilih cabang terlebih dahulu untuk membuat PO.");
    }

    const payload = {
      supplier_id: Number(supplierId),
      store_location_id: Number(storeId),
      order_date: orderDate,
      expected_date: expectedDate || null,
      notes,
      // Qty and price are already in the product's stock unit (Pack for
      // pack-bought goods), so they pass through untouched. No pack scaling
      // here — pack_size is a recipe concern, not a purchasing one.
      items: items.map((it) => ({
        product_id: Number(it.product_id),
        qty_order: Number(it.qty_order),
        unit_price: Number(it.unit_price),
        discount: Number(it.discount || 0),
        tax: Number(it.tax || 0),
      })),
    };

    mutation.mutate(payload);
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-2">
      <div className="bg-white rounded-2xl w-full max-w-5xl max-h-[90vh] flex flex-col overflow-hidden shadow-2xl">
        {/* Header */}
        <div className="p-5 border-b flex items-center justify-between bg-gradient-to-r from-slate-50 to-slate-100">
          <div>
            <div className="text-lg font-semibold text-slate-900">
              Add Purchase (Draft)
            </div>
            <div className="text-xs text-slate-500">
              Buat PO draft, kemudian approve di tabel untuk membuka GR.
            </div>
            {/* {storeId && (
              <div className="mt-1 inline-flex items-center px-2 py-0.5 rounded-full bg-blue-50 text-blue-700 text-[11px] font-medium">
                Product terfilter store: #{storeId}
              </div>
            )} */}
          </div>
          <button
            onClick={onClose}
            className="p-2 rounded-full hover:bg-slate-200 text-slate-600"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Body */}
        <div className="p-5 space-y-5 overflow-y-auto">
          {/* Header form */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            {/* Supplier searchable dropdown */}
            <div className="flex flex-col md:col-span-2 relative">
              <label className="text-xs font-medium text-slate-600 mb-1">
                Supplier
              </label>
              <button
                type="button"
                onClick={() => {
                  setSupplierOpen((v) => !v);
                  setSupplierSearch("");
                  setDebouncedSupplierSearch("");
                }}
                className="w-full px-3 py-2 border rounded-lg text-sm bg-white flex items-center justify-between gap-2 focus:outline-none focus:ring-2 focus:ring-blue-500/70"
              >
                <span
                  className={
                    supplierId ? "text-slate-900" : "text-slate-400"
                  }
                >
                  {supplierId
                    ? findSupplier(supplierId)?.name ||
                      "Supplier tidak ditemukan"
                    : "-- Pilih Supplier --"}
                </span>
                <Search className="w-4 h-4 text-slate-400" />
              </button>

              {supplierOpen && (
                <div className="absolute z-40 mt-1 w-full max-h-72 bg-white border border-slate-200 rounded-xl shadow-lg flex flex-col">
                  <div className="p-2 border-b">
                    <div className="relative">
                      <Search className="w-4 h-4 absolute left-2 top-1/2 -translate-y-1/2 text-gray-400" />
                      <input
                        autoFocus
                        value={supplierSearch}
                        onChange={(e) => setSupplierSearch(e.target.value)}
                        placeholder="Cari nama / kode supplier..."
                        className="w-full pl-7 pr-2 py-1.5 rounded-lg border text-xs focus:outline-none focus:ring-1 focus:ring-blue-500/70"
                      />
                    </div>
                  </div>
                  <div className="flex-1 overflow-y-auto text-sm">
                    {supLoading ? (
                      <div className="px-3 py-2 text-xs text-gray-500">
                        Memuat supplier...
                      </div>
                    ) : filteredSuppliers.length ? (
                      filteredSuppliers.map((s) => (
                        <button
                          key={s.id}
                          type="button"
                          onClick={() => onSelectSupplier(s)}
                          className={`w-full text-left px-3 py-1.5 hover:bg-blue-50 ${
                            String(s.id) === String(supplierId)
                              ? "bg-blue-50"
                              : ""
                          }`}
                        >
                          <div className="text-[13px] font-medium text-slate-800">
                            {s.name}
                          </div>
                          {s.code && (
                            <div className="text-[11px] text-slate-500">
                              {s.code}
                            </div>
                          )}
                        </button>
                      ))
                    ) : (
                      <div className="px-3 py-2 text-xs text-gray-500">
                        Tidak ada supplier yang cocok.
                      </div>
                    )}
                  </div>
                  <div className="p-2 border-t flex justify-end">
                    <button
                      type="button"
                      onClick={() => {
                        setSupplierOpen(false);
                        setSupplierSearch("");
                        setDebouncedSupplierSearch("");
                      }}
                      className="text-[11px] px-2 py-1 rounded border border-slate-200 text-slate-600 hover:bg-slate-50"
                    >
                      Tutup
                    </button>
                  </div>
                </div>
              )}
            </div>

            <div className="flex flex-col">
              <label className="text-xs font-medium text-slate-600 mb-1">
                Order Date
              </label>
              <input
                type="date"
                value={orderDate}
                onChange={(e) => setOrderDate(e.target.value)}
                className="px-3 py-2 border rounded-lg text-sm bg-white focus:outline-none focus:ring-2 focus:ring-blue-500/70"
              />
            </div>

            <div className="flex flex-col">
              <label className="text-xs font-medium text-slate-600 mb-1">
                Expected Date
              </label>
              <input
                type="date"
                value={expectedDate}
                onChange={(e) => setExpectedDate(e.target.value)}
                className="px-3 py-2 border rounded-lg text-sm bg-white focus:outline-none focus:ring-2 focus:ring-blue-500/70"
              />
            </div>

            <div className="flex flex-col md:col-span-4">
              <label className="text-xs font-medium text-slate-600 mb-1">
                Notes
              </label>
              <input
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="Catatan (opsional)"
                className="px-3 py-2 border rounded-lg text-sm bg-white focus:outline-none focus:ring-2 focus:ring-blue-500/70"
              />
            </div>
          </div>

          {/* Items table */}
          <div className="border rounded-2xl overflow-hidden shadow-sm">
            <div className="px-4 py-3 bg-slate-50/80 flex items-center justify-between">
              <div className="text-sm font-semibold text-slate-800">
                Items
              </div>
              <div className="text-xs text-slate-500">
                Produk sudah difilter berdasarkan store user.
              </div>
            </div>

            {/* TIDAK pakai overflow-x-auto di sini supaya portaled dropdown tetap kelihatan */}
            <div className="w-full">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 text-slate-600">
                  <tr>
                    <th className="p-2 text-left min-w-[260px]">Product</th>
                    <th className="p-2 text-right min-w-[100px]">Qty Order</th>
                    <th className="p-2 text-left min-w-[90px]">Satuan</th>
                    <th className="p-2 text-right min-w-[120px]">
                      Unit Price
                    </th>
                    <th className="p-2 text-right min-w-[100px]">Discount</th>
                    <th className="p-2 text-right min-w-[100px]">Tax</th>
                    <th className="p-2 text-right min-w-[140px]">
                      Line Total
                    </th>
                    <th className="p-2 text-center w-[60px]">#</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {items.map((it, idx) => {
                    const lt = subTotals[idx] || 0;
                    const selectedProduct = findProduct(it.product_id);

                    return (
                      <tr key={idx} className="hover:bg-slate-50/60">
                        {/* Product cell with searchable dropdown (portal) */}
                        <td className="p-2 align-top">
                          <button
                            type="button"
                            ref={(el) => (productBtnRefs.current[idx] = el)}
                            onClick={() =>
                              activeProductRow === idx
                                ? (setActiveProductRow(null),
                                  setProductMenuPos(null),
                                  setProductSearch(""),
                                  setDebouncedProductSearch(""))
                                : openProductMenu(idx)
                            }
                            className="w-full px-3 py-1.5 border rounded-lg bg-white text-left text-sm flex items-center justify-between gap-2 focus:outline-none focus:ring-2 focus:ring-blue-500/70"
                          >
                            <span
                              className={
                                it.product_id
                                  ? "text-slate-900"
                                  : "text-slate-400"
                              }
                            >
                              {selectedProduct
                                ? `${selectedProduct.name}${
                                    selectedProduct.sku
                                      ? ` (${selectedProduct.sku})`
                                      : ""
                                  }`
                                : "-- Pilih Produk --"}
                            </span>
                            <Search className="w-4 h-4 text-slate-400" />
                          </button>
                        </td>

                        {/* Qty */}
                        <td className="p-2 text-right align-top">
                          <input
                            type="number"
                            min={1}
                            value={it.qty_order}
                            onChange={(e) =>
                              changeRow(idx, { qty_order: e.target.value })
                            }
                            className="w-24 px-2 py-1.5 border rounded-lg text-right text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/70"
                          />
                          {/* Show the contents this resolves to, so the buyer can
                              sanity-check against the invoice. Purely
                              informational — nothing is rescaled. */}
                          {packSizeOf(selectedProduct) &&
                            toNum(it.qty_order) > 0 && (
                              <div className="mt-1 text-[11px] text-slate-500 whitespace-nowrap">
                                ={" "}
                                {toNum(it.qty_order) *
                                  packSizeOf(selectedProduct)}{" "}
                                {packLabelOf(selectedProduct)}
                              </div>
                            )}
                        </td>

                        {/* Satuan is always the product's stock unit now. A
                            pack-bought product has Unit = Pack, so there is
                            nothing to choose and no conversion to get wrong. */}
                        <td className="p-2 align-top">
                          <div
                            className="px-2 py-1.5 text-sm text-slate-700"
                            title="Satuan mengikuti master produk"
                          >
                            {productUnitLabel(selectedProduct)}
                          </div>
                        </td>

                        {/* Unit Price */}
                        <td className="p-2 text-right align-top">
                          <input
                            type="number"
                            min={0}
                            value={it.unit_price}
                            onChange={(e) =>
                              changeRow(idx, { unit_price: e.target.value })
                            }
                            className="w-28 px-2 py-1.5 border rounded-lg text-right text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/70"
                          />
                        </td>

                        {/* Discount */}
                        <td className="p-2 text-right align-top">
                          <input
                            type="number"
                            min={0}
                            value={it.discount}
                            onChange={(e) =>
                              changeRow(idx, { discount: e.target.value })
                            }
                            className="w-24 px-2 py-1.5 border rounded-lg text-right text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/70"
                          />
                        </td>

                        {/* Tax */}
                        <td className="p-2 text-right align-top">
                          <input
                            type="number"
                            min={0}
                            value={it.tax}
                            onChange={(e) =>
                              changeRow(idx, { tax: e.target.value })
                            }
                            className="w-24 px-2 py-1.5 border rounded-lg text-right text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/70"
                          />
                        </td>

                        {/* Line total */}
                        <td className="p-2 text-right align-top font-semibold text-slate-800">
                          {Number(lt).toLocaleString("id-ID", {
                            style: "currency",
                            currency: "IDR",
                            maximumFractionDigits: 0,
                          })}
                        </td>

                        {/* Remove row */}
                        <td className="p-2 text-center align-top">
                          <button
                            onClick={() => removeRow(idx)}
                            disabled={items.length === 1}
                            className={`inline-flex items-center justify-center w-8 h-8 rounded-full border text-xs ${
                              items.length === 1
                                ? "border-slate-200 text-slate-300 cursor-not-allowed"
                                : "border-red-200 text-red-600 hover:bg-red-50"
                            }`}
                            title="Remove"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            <div className="px-4 py-3 flex flex-col md:flex-row items-start md:items-center justify-between gap-3 bg-slate-50/80">
              <button
                onClick={addRow}
                className="inline-flex items-center gap-2 px-3 py-2 rounded-full bg-white border border-slate-300 text-sm hover:bg-slate-100"
              >
                <Plus className="w-4 h-4" /> Tambah Item
              </button>

              <div className="text-sm md:text-base">
                <span className="text-slate-500 mr-2">Grand Total:</span>
                <span className="font-semibold text-slate-900">
                  {grandTotal.toLocaleString("id-ID", {
                    style: "currency",
                    currency: "IDR",
                    maximumFractionDigits: 0,
                  })}
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="p-5 border-t bg-slate-50 flex items-center justify-end gap-2">
          <button
            onClick={onClose}
            className="px-4 py-2 border rounded-full text-sm text-slate-700 bg-white hover:bg-slate-100"
          >
            Batal
          </button>
          <button
            onClick={submit}
            disabled={mutation.isLoading}
            className="px-4 py-2 rounded-full text-sm font-semibold bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-60"
          >
            {mutation.isLoading ? "Menyimpan..." : "Simpan Draft"}
          </button>
        </div>
      </div>

      {/* === PORTAL DROPDOWN PRODUCT (di luar modal, jadi nggak pernah ke-clip) === */}
      {activeProductRow != null && productMenuPos &&
        createPortal(
          <div
            style={{
              position: "fixed",
              left: productMenuPos.left,
              top: productMenuPos.top,
              width: productMenuPos.width,
              zIndex: 99999,
            }}
            className="max-h-72 bg-white border border-slate-200 rounded-xl shadow-lg flex flex-col"
          >
            <div className="p-2 border-b">
              <div className="relative">
                <Search className="w-4 h-4 absolute left-2 top-1/2 -translate-y-1/2 text-gray-400" />
                <input
                  autoFocus
                  value={productSearch}
                  onChange={(e) => setProductSearch(e.target.value)}
                  placeholder="Cari nama / SKU..."
                  className="w-full pl-7 pr-2 py-1.5 rounded-lg border text-xs focus:outline-none focus:ring-1 focus:ring-blue-500/70"
                />
              </div>
            </div>
            <div className="flex-1 overflow-y-auto text-sm">
              {prodLoading ? (
                <div className="px-3 py-2 text-xs text-gray-500">
                  Memuat produk...
                </div>
              ) : filteredProducts.length ? (
                filteredProducts.map((p) => (
                  <button
                    key={p.id}
                    type="button"
                    onClick={() => onSelectProduct(activeProductRow, p.id)}
                    className={`w-full text-left px-3 py-1.5 hover:bg-blue-50 flex flex-col ${
                      String(p.id) ===
                      String(items[activeProductRow]?.product_id)
                        ? "bg-blue-50"
                        : ""
                    }`}
                  >
                    <span className="text-[13px] font-medium text-slate-800">
                      {p.name}
                    </span>
                    <span className="text-[11px] text-slate-500 flex justify-between gap-2">
                      <span>{p.sku ? `SKU: ${p.sku}` : "Tanpa SKU"}</span>
                      <span>
                        {p.cost_price != null
                          ? `${Number(p.cost_price).toLocaleString("id-ID", {
                              style: "currency",
                              currency: "IDR",
                              maximumFractionDigits: 0,
                            })}/${productUnitLabel(p)}${
                              Number(p.pack_size) > 1
                                ? ` · isi ${Number(p.pack_size)}/${
                                    p.pack_label || "pack"
                                  }`
                                : ""
                            }`
                          : "Cost belum diisi"}
                      </span>
                    </span>
                  </button>
                ))
              ) : (
                <div className="px-3 py-2 text-xs text-gray-500">
                  Tidak ada produk yang cocok.
                </div>
              )}
              {productTruncated && filteredProducts.length > 0 && (
                <div className="px-3 py-1.5 text-[11px] text-amber-600 bg-amber-50 border-t">
                  Menampilkan {products.length} dari {productTotal} produk.
                  Ketik untuk mempersempit pencarian.
                </div>
              )}
            </div>
            <div className="p-2 border-t flex justify-end">
              <button
                type="button"
                onClick={() => {
                  setActiveProductRow(null);
                  setProductMenuPos(null);
                  setProductSearch("");
                  setDebouncedProductSearch("");
                }}
                className="text-[11px] px-2 py-1 rounded border border-slate-200 text-slate-600 hover:bg-slate-50"
              >
                Tutup
              </button>
            </div>
          </div>,
          document.body
        )}
    </div>
  );
}
